import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { of } from "rxjs";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";

import { FamilyAttributeDefinitionResponse } from "../../../pages/admin-pages/products/models/product-models";
import { AdminProductsService } from "../../../pages/admin-pages/products/services/admin-products.service";
import { AdminProductsActions } from "../../actions/admin-products.actions";
import { FailState } from "../../models/fail-state";
import { FamilyAttributesState } from "../../models/mp-admin/family-attributes-state";
import { AppState } from "../../reducers";
import { AdminProductsSelectors } from "../../selectors/admin-products.selectors";

@Injectable()
export class FamilyAttributesEffects {
	familyAttributes = createEffect(() =>
		this.actions.pipe(
			ofType(AdminProductsActions.familyAttributes.get),
			withLatestFrom(this.store.select(AdminProductsSelectors.familyAttributes)),
			switchMap(([action, state]) =>
				this.canUseStoreValue(state, action.familyId)
					? of(AdminProductsActions.familyAttributes.getSuccess({ state, familyId: action.familyId }))
					: of(AdminProductsActions.familyAttributes.getApi({ familyId: action.familyId }))
			)
		)
	);

	familyAttributesApi = createEffect(() =>
		this.actions.pipe(
			ofType(AdminProductsActions.familyAttributes.getApi),
			switchMap((action) => {
				if (!action.familyId) {
					return of(AdminProductsActions.familyAttributes.getFail(new FailState("familyId is required")));
				}
				return this.service.getFamilyAttributes(action.familyId).pipe(
					map((attributes: FamilyAttributeDefinitionResponse[]) => {
						const attributesByFamily: { [familyId: string]: FamilyAttributeDefinitionResponse[] } = {};
						attributes.forEach((attr) => {
							if (!attributesByFamily[attr.productFamilyId]) {
								attributesByFamily[attr.productFamilyId] = [];
							}
							attributesByFamily[attr.productFamilyId].push(attr);
						});

						return AdminProductsActions.familyAttributes.getSuccess({
							state: { attributes, attributesByFamily, loading: false, hasLoaded: true },
							familyId: action.familyId
						});
					}),
					catchError((error) =>
						of(AdminProductsActions.familyAttributes.getFail(new FailState(error)))
					)
				);
			})
		)
	);

	constructor(
		private actions: Actions,
		private service: AdminProductsService,
		private store: Store<AppState>
	) {}

	private canUseStoreValue(state: FamilyAttributesState, familyId?: string): boolean {
		if (
			state === undefined ||
			_.has(state, "error") ||
			state.loading ||
			state.hasLoaded !== true ||
			!_.has(state, "attributes") ||
			state.attributes === undefined
		) {
			return false;
		}

		if (!familyId) {
			return true;
		}

		return (
			_.has(state, "attributesByFamily") &&
			state.attributesByFamily !== undefined &&
			_.has(state.attributesByFamily, familyId)
		);
	}
}
